import { useMutation } from '@tanstack/react-query';
import { updateProfile, updateProfileImage } from '@/lib/api/profile';
import { uploadFile } from '@/lib/api/fileUpload';
import type { ProfileFormValues } from '../validations/profileSchema';
import { queryClient } from '@/queries';
import { QUERY_KEYS } from '@/lib/constants';

export interface UpdateProfileParams {
    data: ProfileFormValues;
    profileImageUrl?: string;
}

/**
 * Mutation hook for updating user profile
 */
export function useUpdateProfile() {
    return useMutation({
        mutationFn: ({ data, profileImageUrl }: UpdateProfileParams) => updateProfile(data, profileImageUrl),
        onSuccess: async () => {
            // Invalidate user query to refetch fresh data
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
        },
    });
}

/**
 * Mutation hook for uploading profile image to S3
 */
export function useUploadProfileImage() {
    return useMutation({
        mutationFn: (file: File) => uploadFile(file, { objectType: 'profile-image' }),
    });
}

/**
 * Mutation hook for updating user profile image
 */
export function useUpdateProfileImage() {
    return useMutation({
        mutationFn: (profileImageUrl: string) => updateProfileImage(profileImageUrl),
        onSuccess: async () => {
            // Invalidate user query to refetch fresh data
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
        },
    });
}
